import { useState, useEffect } from "react";
import api from "../utils/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import WhatsNewCard from "./WhatsNewCard";

/**
 * App-wide "What's New" popup. Shows each published announcement the user
 * hasn't seen exactly ONCE (per-user, cross-device — backed by ui-flags).
 */
export default function WhatsNewPopup() {
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/whats-new/unseen");
        const items = res.data?.announcements || [];
        if (!cancelled && items.length > 0) {
          setQueue(items);
          setIndex(0);
          setOpen(true);
        }
      } catch { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const current = queue[index];

  // Mark the current announcement as seen the moment it's displayed, so the
  // user genuinely only ever sees it once (even if they close via the X).
  useEffect(() => {
    if (open && current?.id) {
      api.post(`/ui-flags/whatsnew_${current.id}`).catch(() => {});
    }
  }, [open, current?.id]);

  if (!current) return null;

  const advance = () => {
    if (index + 1 < queue.length) setIndex(index + 1);
    else setOpen(false);
  };

  const handleCta = () => {
    const url = current.button_url;
    if (url.startsWith("http")) window.open(url, "_blank", "noopener");
    else window.location.href = url;
    advance();
  };

  const isLast = index + 1 >= queue.length;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) advance(); }}>
      <DialogContent className="p-0 overflow-hidden sm:max-w-[440px] max-h-[90vh] flex flex-col" data-testid="whats-new-popup">
        <DialogTitle className="sr-only">What's New</DialogTitle>
        <DialogDescription className="sr-only">Latest product update from QuoteSorted</DialogDescription>
        <WhatsNewCard
          announcement={current}
          counter={queue.length > 1 ? `${index + 1} / ${queue.length}` : null}
          dismissLabel={isLast ? "Got it" : "Next"}
          onCta={handleCta}
          onDismiss={advance}
        />
      </DialogContent>
    </Dialog>
  );
}
